import { ZA, GH, ZM, TZ, MW } from 'country-flag-icons/string/3x2';
import type { ISitemapRemap } from '~/interfaces/dto/sitemap/response';
import type { IAction, IActionKey } from '~/interfaces/form/grecaptcha';
import type { FeatureFlag } from '~/interfaces/dto/config/response';
import type { PublicRuntimeConfig } from '@nuxt/schema';
import type { FirebaseOptions } from '@firebase/app-types';

export const regions = [
  {
    name: 'South Africa',
    code: 'ZA',
    regionCode: 'JZA',
    flag: import.meta.dev ? ZA : null,
  },
  {
    name: 'Ghana',
    code: 'GH',
    regionCode: 'JGH',
    flag: import.meta.dev ? GH : null,
  },
  {
    name: 'Zambia',
    code: 'ZM',
    regionCode: 'JZM',
    flag: import.meta.dev ? ZM : null,
  },
  {
    name: 'Tanzania',
    code: 'TZ',
    regionCode: 'JTZ',
    flag: import.meta.dev ? TZ : null,
  },
  {
    name: 'Malawi',
    code: 'MW',
    regionCode: 'JMW',
    flag: import.meta.dev ? MW : null,
  },
];

export const brandIds = {
  JTZ: 'E430B4FB-55DC-42CC-AA65-1057CE49C7E6',
  JAO: 'FEE7672B-53BA-45B7-823F-1E61A9B9509B',
  JKE: 'B33B59F1-E3D4-449A-9696-2201A185A858',
  JET: '5A533B6D-80CC-42D0-802E-804E3662540A',
  JCM: 'F86AFCDE-0FCB-4197-9207-87EFD66409A1',
  JZM: 'D17024C3-F417-4D1C-B142-9E5F9E83B49B',
  JMZ: 'CFF8B795-9939-432F-A73D-B2393F8E658D',
  JZA: 'D76A0E62-B728-4A28-8134-C57A1A003199',
  JUG: '75EA7131-252E-44C3-B369-CC6DA7192FAE',
  JNG: 'DBAE2A59-1E73-4DA2-B169-CDA0239BCF78',
  JMW: 'CA968023-3E8E-43BC-B3AA-DB01C469A4C6',
  JGH: 'BE6CDE0B-73E4-448A-A706-F9C20CF3F669',
};

export const defaultLocales = {
  JTZ: 'sw-TZ',
  JAO: 'en-US',
  JKE: 'sw-KE',
  JET: 'en-US',
  JCM: 'en-US',
  JZM: 'en-US',
  JMZ: 'pt-MZ',
  JZA: 'en-US',
  JUG: 'en-US',
  JNG: 'en-US',
  JMW: 'en-US',
  JGH: 'en-US',
};

export const subdomainLocales = {
  en: 'en-US',
  sw: 'sw-TZ',
  pt: 'pt-MZ',
};

export const brandDomainSuffix = {
  JTZ: 'co.tz',
  JAO: 'co.ag',
  JKE: 'co.ke',
  JET: 'co.et',
  JCM: 'cm',
  JZM: 'co.zm',
  JMZ: 'com.mz',
  JZA: 'co.za',
  JUG: 'ug',
  JNG: 'com.ng',
  JMW: 'mw',
  JGH: 'com.gh',
};

export const brandCurrencies = {
  JZA: {
    currencyCode: 'ZAR',
    currencyName: 'South African Rand',
    currencyShortName: 'South African Rand',
    currencySymbol: 'R',
  },
  JMW: {
    currencyCode: 'MWK',
    currencyName: 'Malawian Kwacha',
    currencyShortName: 'Malawian Kwacha',
    currencySymbol: 'MK',
  },
  JTZ: {
    currencyCode: 'TZS',
    currencyName: 'Tanzanian Shilling',
    currencyShortName: 'Tanzanian Shilling',
    currencySymbol: 'TSh',
  },
  JGH: {
    currencyCode: 'GHS',
    currencyName: 'Ghanaian New Cedi',
    currencyShortName: 'Ghanaian New Cedi',
    currencySymbol: 'GHS',
  },
  JZM: {
    currencyCode: 'ZMW',
    currencyName: 'Zambian Kwacha',
    currencyShortName: 'Zambian Kwacha',
    currencySymbol: 'ZMW',
  },
};

export const brandCountryDetails = {
  JZA: {
    countryDialingCode: '+27',
    countryIsoThree: 'ZAF',
    countryIsoTwo: 'ZA',
    countryName: 'South Africa',
  },
  JMW: {
    countryDialingCode: '+265',
    countryIsoThree: 'MWI',
    countryIsoTwo: 'MW',
    countryName: 'Malawi',
  },
  JTZ: {
    countryDialingCode: '+255',
    countryIsoThree: 'TZA',
    countryIsoTwo: 'TZ',
    countryName: 'Tanzania',
  },
  JGH: {
    countryDialingCode: '+233',
    countryIsoThree: 'GHA',
    countryIsoTwo: 'GH',
    countryName: 'Ghana',
  },
  JZM: {
    countryDialingCode: '+260',
    countryIsoThree: 'ZMB',
    countryIsoTwo: 'ZM',
    countryName: 'Zambia',
  },
};

export const genesysChatIds = {
  JTZ: 'd709eeec-4aa0-42d8-94f3-84f85bac8c86',
  JMW: '3fec1803-d02d-4ad1-aa69-cc4dcf0f83c1',
  JZA: '7ea4cc55-5c6a-4269-9866-9f746c2f9e88',
  JGH: 'edf28659-934c-4e8a-b4cc-1933cedaeb0c',
  JZM: '',
};

export const genesysQueueName = {
  JTZ: 'BWA Jackpot City Swahili Tanzania Chat Support',
  JMW: 'BWA Jackpot City English Malawi Chat Support',
  JZA: 'BWA Jackpot City English SA Chat Support',
  JGH: 'BWA Jackpot City English Ghana Chat Support',
  JZM: '',
};

export const socialLinks = {
  JZA: {
    facebook: 'https://www.facebook.com/JackpotCitySouthAfrica',
    instagram: 'https://www.instagram.com/jackpotcitysa',
    x: 'https://x.com/JackPotCitySA',
  },
  JTZ: {
    facebook: 'https://www.facebook.com/JackpotCityCasinoTanzania',
    instagram: 'https://www.instagram.com/jackpotcitycasinotz/',
    x: 'https://x.com/JPCCasinoTZ',
  },
  JMW: {
    facebook: 'https://www.facebook.com/JackpotCityCasinoMalawi',
    instagram: 'https://www.instagram.com/jackpotcitycasinomw',
    x: 'https://x.com/JPCCasinoMW',
  },
  JGH: {
    facebook: 'https://www.facebook.com/JackpotCityCasinoGhana',
    instagram: 'https://www.instagram.com/jackpotcitycasinoghana/',
    x: 'https://x.com/JackPotCity_gh',
  },
  JZM: {
    facebook: 'https://www.facebook.com/JackpotCityCasinoZambia',
    instagram: 'https://www.instagram.com/jackpotcityzambia/',
    x: 'https://x.com/JackpotCityZM',
  },
};

export const socialMeta = {
  JZA: {
    related: '@JackPotCitySA',
  },
  JTZ: {
    related: '@JPCCasinoTZ',
  },
  JMW: {
    related: '@JPCCasinoMW',
  },
  JGH: {
    related: '@JackPotCity_gh',
  },
  JZM: {
    related: '@JPCCasinoZM',
  },
};

export const captchaV3SiteKey = {
  production: '6LcHT7kqAAAAAAoNyyMMxjOqAzJGPiBVGaS4aiPt',
  uat: '6LcHT7kqAAAAAAoNyyMMxjOqAzJGPiBVGaS4aiPt',
  development: '6LdPZ9IqAAAAANfhbdCicisV3JEzbW6Ej3NTpJAe',
  qa: '6LdPZ9IqAAAAANfhbdCicisV3JEzbW6Ej3NTpJAe',
  staging: '6LdPZ9IqAAAAANfhbdCicisV3JEzbW6Ej3NTpJAe',
};

export const captchaV3Actions = new Map<IActionKey, IAction>([
  ['login', { action: 'AUTHENTICATE' }],
  ['register', { action: 'REGISTER' }],
  ['update-password', { action: 'PASSWORD_CHANGE_INITIATE' }],
]);

export const defaultFeatureFlags = {
  // website
  'website.enablefreeplay': false,
  'website.enablerecaptcha': true,
  'website.toastmessages': true,
  'website.cookiebar': true,
  'website.enableappdownload': true,
  'website.truncatecurrency': true,
  'website.enablesecondarynavigation': false,
  'website.enablesocialicons': true,
  'website.notificationpanel': true,
  'website.livechat': true,
  // responsible gaming
  'responsiblegaming.wagerlimit': true,
  'responsiblegaming.sessionlimit': true,
  'responsiblegaming.coolingoffperiod': true,
  'responsiblegaming.selfexclusion': true,
  // my account
  'myaccount.cashbalance': true,
  'myaccount.bonusbalance': true,
  'myaccount.totalbalance': true,
  'myaccount.enablewithdrawalrefund': true,
};

export const overrideFeatureFlags = {
  JZA: {
    'website.enablewelcomeoffer': true,
  },
};

export const temporaryFeatureFlags: FeatureFlag[] = [
  {
    key: 'miniGames',
    name: 'Mini Games',
    description: 'Enable mini games or preloaded games',
    features: [
      {
        id: 999,
        key: 'enableBetGamesMiniGame',
        name: 'Enable Bet Games Mini Game',
        description: 'Enable Bet Games Mini Game',
        enabled: false,
      },
    ],
  },
];

export const defaultSitemap: Record<string, Partial<ISitemapRemap>> = {
  '/': {
    nuxtRouteParams: { name: '', params: {}, path: '/' },
    nuxtSeoParams: {
      title: 'Jackpot City | Online Betting',
      description: 'Go all in at Jackpot City. Spin your way to the Jackpot!',
      ogTitle: 'Jackpot City | Online Betting',
      twitterTitle: 'Jackpot City | Online Betting',
      twitterDescription:
        'Go all in at Jackpot City. Spin your way to the Jackpot!',
      ogDescription: 'Go all in at Jackpot City. Spin your way to the Jackpot!',
      twitterCard: 'summary',
      keywords: null,
    },
    Path: '/',
    Navigation: {
      top: true,
      footer: false,
      left: false,
      secondary: false,
      blog: false,
    },
    PageName: 'Home',
    Alias: 'home',
    DocumentPageDescription:
      'Go all in at Jackpot City. Spin your way to the Jackpot!',
    DocumentPageKeyWords: null,
    DocumentPageTitle: 'Jackpot City | Online Betting',
    Mapping: 'Home',
    MobileApp: { enabled: true, component: 'HomeComponent', content: null },
    Children: [],
  },
};

export const environmentVariables: Record<
  string,
  Record<keyof PublicRuntimeConfig, unknown>
> = {
  development: {
    kentico: 'https://dev-gatewayapi.osiristrading.net/kentico-dev',
    authApi: 'https://dev-app.jpc.africa/auth',
    balance: 'https://dev-app.jpc.africa/balance',
    rewards: 'https://dev-loyalty-external.betwayafrica.com',
    casinoBonus: 'https://dev-casinobonusing.jpc.africa',
    config: 'https://dev-config.jpc.africa',
    promotions: 'https://promoapi-dev.jpc.africa',
    environment: 'development',
    bankingEnvironment: 'dev.jackpotcity',
  },
  qa: {
    kentico: 'https://dev-gatewayapi.osiristrading.net/kentico-dev',
    authApi: 'https://uat-app.jpc.africa/auth',
    balance: 'https://uat-app.jpc.africa/balance',
    rewards: 'https://uat-app.easterndawn.io/loyaltyexternal',
    casinoBonus: 'https://uat-casinobonusing.jpc.africa',
    config: 'https://dev-config.jpc.africa',
    promotions: 'https://promoapi-dev.jpc.africa',
    environment: 'staging',
    bankingEnvironment: 'qa.jackpotcity',
  },
  uat: {
    kentico: 'https://cms1.jpc.africa',
    authApi: 'https://app.jpc.africa/auth',
    balance: 'https://app.jpc.africa/balance',
    rewards: 'https://loyalty-external.jpc.africa',
    casinoBonus: 'https://casinobonusing.jpc.africa',
    config: 'https://uat-config.jpc.africa',
    promotions: 'https://promoapi.jpc.africa',
    environment: 'uat',
    bankingEnvironment: 'www.jackpotcity',
  },
  ppe: {
    kentico: 'https://cms1.jpc.africa',
    authApi: 'https://ppe-app.jpc.africa/auth',
    balance: 'https://ppe-app.jpc.africa/balance',
    rewards: 'https://loyalty-external.jpc.africa',
    casinoBonus: 'https://casinobonusing.jpc.africa',
    config: 'https://ppe-uat-config.jpc.africa',
    promotions: 'https://promoapi.jpc.africa',
    environment: 'production',
    bankingEnvironment: 'www.jackpotcity',
  },
  // proxy urls are NOT used in production, only on local development
  production: {
    kentico: 'https://cms1.jpc.africa',
    authApi: 'https://app.jpc.africa/auth',
    balance: 'https://app.jpc.africa/balance',
    rewards: 'https://app.jpc.africa/rewards',
    casinoBonus: 'https://app.jpc.africa/casinobonusing',
    config: 'https://config.jpc.africa',
    promotions: 'https://promoapi.jpc.africa',
    environment: 'production',
    bankingEnvironment: 'www.jackpotcity',
  },
};

export const passwordRegex = {
  JZA: { strong: '^(.){8,20}$', medium: '^(.){1,20}$' },
  JMW: { strong: '^(.){8,20}$', medium: '^(.){1,20}$' },
  JTZ: { strong: '^(.){4,20}$', medium: '^(.){1,20}$' },
  JZM: { strong: '^(.){8,20}$', medium: '^(.){1,20}$' },
  JGH: { strong: '^(.){8,20}$', medium: '^(.){1,20}$' },
};

export const regionPasswordSuggestions = {
  JZA: [{ min: 8 }, { max: 20 }, { vulnerable: true }],
  JMW: [{ min: 8 }, { max: 20 }, { vulnerable: true }],
  JTZ: [{ min: 4 }, { max: 20 }, { vulnerable: true }],
  JZM: [{ min: 8 }, { max: 20 }, { vulnerable: true }],
  JGH: [{ min: 8 }, { max: 20 }, { vulnerable: true }],
};

export const defaultRedirects: { fromLink: string; toLink: string }[] = [
  { fromLink: '/home/featured/lucky-numbers', toLink: '/luckynumbers' },
  {
    fromLink: '/location-and-interest-survey',
    toLink: '/campaigns/location-and-interest-survey',
  },
  {
    fromLink: '/favourite-games-survey',
    toLink: '/campaigns/favourite-games-survey',
  },
  {
    fromLink: '/campaigns',
    toLink: '/campaigns/register',
  },
  {
    fromLink: '/partners/register/12-masks-fire-drums',
    toLink: '/campaigns/12-masks-fire-drums',
  },
];

export const changeWagerLimitCooldown = 7;

export const appleAppIds = {
  JTZ: '6738078345',
  JMW: '6720722117',
  JZA: '6476136241',
};

export const wheelUrls = {
  JTZ: '/karibu-wheel',
  JMW: '/fikani-wheel',
};

export const loginRoutes = ['/login', '/sign-in'];
export const registerRoutes = [
  '/register',
  '/sign-up',
  '/partners/register',
  '/partners/signup',
  '/partners/sign-up',
  '/partners/streaming-academy/register',
  '/partners/streaming-academy/sign-up',
  '/partners/streaming-academy/signup',
];

export const explicitLoggedOutRoutes = [...loginRoutes, ...registerRoutes];

export const contentRoutes = [
  '/promotions',
  '/big-city-life',
  '/blog',
  '/favorites',
  '/winners',
  '/winners-circle',
  '/suggest-and-win',
  '/get-the-app',
  '/how-to',
  '/new-games',
  '/luckynumbers',
  '/landing',
  '/share',
  '/gifts',
];
export const systemRoutes = [
  '/playground',
  '/banner-playground',
  '/health',
  '/~partytown',
];
export const partnerRoutes = ['/partners', '/partners/register'];

export const partialRegDefaultTimers = {
  otpTimeout: 180000,
  timeBeforeAccountDeletion: 1800000,
};

export const regionIdRegex: Record<
  string,
  {
    idCard?: string;
    idNumber?: string;
    passport?: string;
    voterId?: string;
    oldVoterId?: string;
    driversLicense?: string;
    oldDriversLicense?: string;
    socialSecurityNumber?: string;
  }
> = {
  JZA: {
    idNumber: '^[0-9]{13}$',
    passport: '^(?=.*[A-Za-z0-9])[A-Za-z0-9 .-]{6,20}$',
  },
  JGH: {
    idCard: '^[A-Za-z]{3}?[0-9]{9}?[0-9]$',
    // idCard: '^[A-Za-z]{3}-?[0-9]{9}-?[0-9]$',
    passport: '^(?=.*[A-Za-z0-9])[A-Za-z0-9 .-]{6,20}$',
    voterId: '^[0-9]{10,12}$',
    oldVoterId: '^[0-9]{10,12}$',
    driversLicense: '^[a-zA-Z0-9]{6,10}$',
    oldDriversLicense: '^[a-zA-Z-0-9]{2,10}$',
    socialSecurityNumber: '^[A-Za-z]{1}[A-Za-z0-9]{12,14}$',
  },
};

export const rewardsFormattingLimits = {
  decimalLimit: 2,
  bannerHideDecimalsThreshold: 9999,
  bannerTruncateThreshold: 99999,
  graphTruncateThreshold: 9999999,
  graphHideDecimalsThreshold: 9999999,
  summaryHideDecimalsThreshold: 99999,
  summaryTruncateThreshold: 99999,
};

export const marketingSessionPlatformIds = {
  mobile: 1,
  tablet: 1,
  desktop: 2,
  ios: 3,
  android: 4,
  huawei: 5,
};

export const marketingSessionRegionIds = {
  JZA: 13,
  JMW: 14,
  JTZ: 15,
  JGH: 17,
};

export const categoryOverrides = {
  quickgames: 'quick-games',
  crashgames: 'crash-games',
};

export const firebaseConfig: Partial<FirebaseOptions> = {
  apiKey: 'AIzaSyD1AwLRbbllLKPd11rvU1EUF4qpzyKYhF0',
  projectId: 'jackpot-city-e728f',
  messagingSenderId: '70165644592',
  appId: '1:70165644592:web:fa261e154f812755e074f5',
};
